import { initializeApp, deleteApp } from "firebase/app"
import {
  getAuth,
  signInWithPopup,
  signInWithCustomToken,
  signOut,
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
  GoogleAuthProvider,
  GithubAuthProvider,
  OAuthProvider,
  SAMLAuthProvider,
  AuthProvider as FBAuthProvider,
} from "firebase/auth"
import { firebaseConfig } from "../../firebase/init"
import { getService } from "@hoppscotch/common/modules/dioc"
import { OrganizationService } from "../../services/organization.service"
import {
  getOrganizationDomain,
  getRootDomain,
} from "../utils/cloud-for-orgs"
import * as E from "fp-ts/Either"
import { runGQLQuery } from "@hoppscotch/common/helpers/backend/GQLClient"
import { GetOrganizationInfoByDomainDocument } from "../backend/graphql"

export type OrgProviderType =
  | "GOOGLE"
  | "GITHUB"
  | "MICROSOFT"
  | "MAGIC_LINK"
  | "SAML"
  | "OIDC"
  | string

export type OrgProviderEntry = {
  provider: OrgProviderType
  providerID?: string
  label?: string
}

export type OrgProvidersResponse = {
  orgID?: string | null
  gcipTenantID: string | null
  providers: OrgProviderEntry[]
  // Set on the defaults returned after a failed lookup, where the org's real
  // tenant is unknown, so callers look it up again before trusting it.
  isFallback?: boolean
}

export type OrgSignInResult = {
  ok: boolean
  customToken?: string
  email?: string | null
}

export type SignIntoOrgOptions = {
  rootToken?: string | null
}

const DEFAULT_PROVIDERS: OrgProviderEntry[] = [
  { provider: "GOOGLE" },
  { provider: "GITHUB" },
  { provider: "MICROSOFT" },
  { provider: "MAGIC_LINK" },
]

let activeOrgProviders: OrgProvidersResponse | null = null

export function getActiveOrgProviders(): OrgProvidersResponse | null {
  return activeOrgProviders
}

/**
 * Returns the appropriate Firebase AuthProvider instance for an entry from
 * GET /v1/auth/providers.
 */
export function providerFor(entry: OrgProviderEntry): FBAuthProvider {
  const pid = entry.providerID

  if (pid?.startsWith("saml.")) {
    return new SAMLAuthProvider(pid)
  }

  if (pid?.startsWith("oidc.")) {
    return new OAuthProvider(pid)
  }

  const p = entry.provider.toUpperCase()

  if (p === "SAML") {
    return new SAMLAuthProvider(pid || "saml.sso")
  }

  if (p === "OIDC") {
    return new OAuthProvider(pid || "oidc.sso")
  }

  if (p === "GOOGLE") {
    return new GoogleAuthProvider()
  }

  if (p === "GITHUB") {
    return new GithubAuthProvider().addScope("gist")
  }

  if (p === "MICROSOFT") {
    return new OAuthProvider("microsoft.com")
  }

  throw new Error(`no popup flow for ${entry.provider}`)
}

/**
 * Whether sign-in uses root Firebase auth without an org lookup: on the
 * default cloud instance, where there is no org domain, and on an org whose
 * cached providers have no GCIP tenant. Defaults cached after a failed
 * lookup don't count, since the org's tenant is unknown then.
 */
export function shouldUseRootAuth(
  orgDomain: string | null,
  cached: OrgProvidersResponse | null
): boolean {
  if (!orgDomain) return true
  return !!cached && !cached.isFallback && !cached.gcipTenantID
}

/**
 * Whether a tenant sign-in error means the org's tenant hasn't enabled that
 * provider, the case where sign-in falls back to root auth.
 */
export function isTenantProviderDisabled(err: unknown): boolean {
  const e = err as { code?: unknown } | null
  const code = String(
    e?.code ?? (err instanceof Error ? err.message : String(err))
  )
  return (
    code.includes("operation-not-allowed") ||
    code.includes("configuration-not-found")
  )
}

/**
 * Discovers available sign-in methods and GCIP tenant ID for a domain.
 * Calls public GET /v1/auth/providers?domain=<domain> (or without domain for plain cloud).
 */
export async function fetchOrgProviders(
  domain?: string | null
): Promise<OrgProvidersResponse> {
  let targetDomain = domain || getOrganizationDomain()
  if (!targetDomain && typeof window !== "undefined") {
    try {
      const orgService = getService(OrganizationService)
      targetDomain =
        (orgService.organizationInfo.value as any)?.orgDomain ?? null
    } catch {
      // ignore
    }
    if (!targetDomain) {
      targetDomain = new URLSearchParams(window.location.search).get("org")
    }
  }

  const baseUrl = (import.meta.env.VITE_BACKEND_API_URL ?? "").replace(
    /\/+$/,
    ""
  )

  const url = targetDomain
    ? `${baseUrl}/auth/providers?domain=${encodeURIComponent(targetDomain)}`
    : `${baseUrl}/auth/providers`

  try {
    const res = await fetch(url)

    if (!res.ok) {
      if (res.status === 404) {
        // Cache the default providers before rethrowing, so the next sign-in
        // click opens the popup without waiting on another lookup.
        activeOrgProviders = {
          orgID: null,
          gcipTenantID: null,
          providers: DEFAULT_PROVIDERS,
        }
        throw new Error("organization/organization_does_not_exist")
      }
      console.warn(
        `[org-session] Failed to fetch providers (${res.status} ${res.statusText}), using defaults`
      )
      const fallback: OrgProvidersResponse = {
        orgID: null,
        gcipTenantID: null,
        providers: DEFAULT_PROVIDERS,
        isFallback: true,
      }
      activeOrgProviders = fallback
      return fallback
    }

    const data: OrgProvidersResponse = await res.json()

    // The org ID is only used for tenant sign-in, so an org with no GCIP
    // tenant skips the by-domain lookup.
    if (!data.orgID && data.gcipTenantID) {
      try {
        const orgService = getService(OrganizationService)
        let orgId =
          (orgService.organizationInfo.value as any)?.orgID ??
          orgService.getOrganizationID()
        if (!orgId && targetDomain) {
          const gqlRes = await runGQLQuery({
            query: GetOrganizationInfoByDomainDocument,
            variables: { orgDomain: targetDomain },
            skipOrgInfoWait: true,
          })
          if (E.isRight(gqlRes)) {
            const domainOrg =
              gqlRes.right.organizationByDomainV2 ??
              (gqlRes.right as any).organizationByDomain
            orgId = typeof domainOrg === "object" ? domainOrg?.orgID : domainOrg
          }
        }
        if (orgId) {
          data.orgID = orgId
        }
      } catch (err) {
        console.warn("[org-session] Could not resolve orgID for providers:", err)
      }
    }

    activeOrgProviders = data
    return data
  } catch (error) {
    if (
      error instanceof Error &&
      error.message === "organization/organization_does_not_exist"
    ) {
      throw error
    }

    console.error("[org-session] fetchOrgProviders error:", error)
    const fallback: OrgProvidersResponse = {
      orgID: null,
      gcipTenantID: null,
      providers: DEFAULT_PROVIDERS,
      isFallback: true,
    }
    activeOrgProviders = fallback
    return fallback
  }
}

/**
 * Signs into an organization with an isolated throwaway broker Firebase app.
 *
 * Runs the tenant-scoped sign-in on a temporary app with brokerAuth.tenantId = gcipTenantID
 * so that onIdTokenChanged does NOT fire on the main getAuth() instance, preventing
 * session pollution and preserving in-flight request headers.
 *
 * Exchanging the tenant ID token for a root-scoped session or confirming IdP verification
 * is handled via POST /v1/auth/org-session.
 */
export async function signIntoOrg(
  orgID: string,
  gcipTenantID: string,
  entry: OrgProviderEntry,
  options?: SignIntoOrgOptions
): Promise<OrgSignInResult> {
  // 1. A throwaway broker app.
  const brokerName = `broker-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
  const broker = initializeApp(firebaseConfig, brokerName)
  const brokerAuth = getAuth(broker)
  brokerAuth.tenantId = gcipTenantID

  try {
    // 2. Complete popup sign-in on the tenant auth instance
    const cred = await signInWithPopup(brokerAuth, providerFor(entry))
    const tenantToken = await cred.user.getIdToken()
    const email = cred.user.email

    // 3. Obtain caller's existing root session if available
    const main = getAuth()
    let rootToken: string | null | undefined = options?.rootToken
    if (rootToken === undefined) {
      rootToken = main.currentUser
        ? await main.currentUser.getIdToken()
        : null
    }

    // 4. Send token exchange / session record request to backend
    const data = await exchangeTenantToken(orgID, tenantToken, rootToken)

    return {
      ok: true,
      customToken: data.customToken,
      email,
    }
  } finally {
    // 5. Tear down throwaway broker app completely
    await signOut(brokerAuth).catch(() => {})
    await deleteApp(broker).catch(() => {})
  }
}

/**
 * Exchanges a GCIP tenant token for an org session by calling POST /auth/org-session.
 * If a customToken is returned, authenticates the main Firebase auth instance with it.
 *
 * NOTE: For email links, rootToken MUST NOT be provided so that backend exchanges
 * unconditionally for customToken, preventing user session confusion.
 */
export async function exchangeTenantToken(
  orgID: string,
  tenantToken: string,
  rootToken?: string | null
): Promise<{ customToken?: string; ok?: boolean }> {
  const baseUrl = (import.meta.env.VITE_BACKEND_API_URL ?? "").replace(
    /\/+$/,
    ""
  )
  const res = await fetch(`${baseUrl}/auth/org-session`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({
      orgID,
      tenantToken,
      ...(rootToken ? { rootToken } : {}),
    }),
  })

  if (!res.ok) {
    const errorData = await res.json().catch(() => ({}))
    const code =
      errorData.message || res.statusText || "auth/handshake_failed"
    throw new Error(code)
  }

  const data: { customToken?: string; ok?: boolean } = await res.json()

  if (data.customToken) {
    await signInWithCustomToken(getAuth(), data.customToken)
  }

  return data
}

/**
 * Emails a sign-in link scoped to the org's tenant. The link lands on the
 * default cloud instance's /enter (the only continue URL Firebase trusts),
 * which forwards it to this subdomain's /orgs/email-link. A page on the
 * default cloud instance itself, like `/device-login`, passes its own URL as
 * `returnUrl` and completes the link with `signIntoOrgWithEmailLink`.
 */
export async function sendOrgEmailLink(
  gcipTenantID: string,
  email: string,
  returnUrl?: string
): Promise<void> {
  const broker = initializeApp(
    firebaseConfig,
    `broker-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
  )
  const brokerAuth = getAuth(broker)
  brokerAuth.tenantId = gcipTenantID

  try {
    if (returnUrl) {
      await sendSignInLinkToEmail(brokerAuth, email, {
        url: returnUrl,
        handleCodeInApp: true,
      })
      return
    }

    const rawBase =
      import.meta.env.VITE_BASE_URL ||
      (typeof window !== "undefined" && window.location?.origin
        ? window.location.origin
        : "https://hoppscotch.io")
    const baseUrl = rawBase.startsWith("http") ? rawBase : `https://${rawBase}`
    const continueUrl = new URL(`${baseUrl.replace(/\/+$/, "")}/enter`)
    const orgDomain = getOrganizationDomain()
    const rootDomain = getRootDomain()
    const targetHost =
      orgDomain && rootDomain
        ? `${orgDomain}.${rootDomain}`
        : (orgDomain ?? rootDomain ?? "")
    continueUrl.searchParams.set(
      "redirect",
      `${targetHost}/orgs/email-link`.replace(/^\/+/, "")
    )

    await sendSignInLinkToEmail(brokerAuth, email, {
      url: continueUrl.href,
      handleCodeInApp: true,
    })
  } finally {
    await deleteApp(broker).catch(() => {})
  }
}

/**
 * Completes a tenant email link on the page it returned to, the way
 * `/orgs/email-link` does on an org subdomain. The org session is recorded
 * for the address the link was sent to, and the main Firebase app is signed
 * in as that account.
 *
 * `rootToken` is the session of the account the link was sent to, where that
 * account is already signed in, which lets the backend join an existing
 * Hoppscotch account to the org as the provider sign-in does. Without it the
 * backend returns a custom token for the address.
 */
export async function signIntoOrgWithEmailLink(
  orgID: string,
  gcipTenantID: string,
  email: string,
  href: string,
  rootToken: string | null = null
): Promise<void> {
  const broker = initializeApp(
    firebaseConfig,
    `broker-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`
  )
  const brokerAuth = getAuth(broker)
  brokerAuth.tenantId = gcipTenantID

  try {
    if (!isSignInWithEmailLink(brokerAuth, href)) {
      throw new Error("auth/invalid-action-code")
    }

    const cred = await signInWithEmailLink(brokerAuth, email, href)
    const tenantToken = await cred.user.getIdToken()

    await exchangeTenantToken(orgID, tenantToken, rootToken)
  } finally {
    await signOut(brokerAuth).catch(() => {})
    await deleteApp(broker).catch(() => {})
  }
}

/**
 * Returns a human-friendly message for errors encountered during the org session handshake.
 */
export function formatOrgSessionError(error: unknown): string {
  const code =
    (error as any)?.code ||
    (error instanceof Error ? error.message : String(error))

  switch (code) {
    case "organization/domain_not_whitelisted":
      return "Your email domain isn't allowed to join this organization. Ask an admin for an invite."
    case "organization/organization_member_not_invited":
      return "This email isn't a member of this organization. Ask your admin to invite you."
    case "auth/email_unverified":
      return "We couldn't verify this email. Please request a new sign-in link."
    case "auth/link_wrong_org":
    case "auth/tenant-id-mismatch":
      return "This sign-in link belongs to a different organization."
    case "auth/invalid-action-code":
      return "This sign-in link has expired or was already used. Please request a new one."
    case "auth/operation-not-allowed":
      return "Email sign-in is turned off for this organization."
    case "auth/quota-exceeded":
      return "Too many sign-in emails right now. Please try again later."
    case "auth/link_required":
      return "An account with this email already exists. Please sign in with your existing account first to link it with your organization."
    case "scim/not_provisioned":
      return "Your account hasn't been set up for this organization yet. Ask your IT admin to assign you to Hoppscotch."
    case "auth/different_account":
      return "You are signed in with a different account than the one linked to this identity provider. Please switch accounts and try again."
    case "auth/email_missing":
      return "Your Identity Provider did not provide an email address. Please contact your organization administrator."
    case "auth/tenant_token_invalid":
      return "The authentication token is invalid for this organization. Please try signing in again."
    case "organization/organization_member_inactive":
      return "Your organization membership is inactive. Please contact your organization administrator."
    case "organization/member_seats_max_limit":
    case "organization/organization_member_max_limit":
    case "organization/organization_member_seats_max_limit":
      return "The organization has reached its maximum seat limit. Please contact your organization administrator."
    case "sso/required":
      return "Single Sign-On is required to access this organization."
    case "sso/tenant_not_provisioned":
      return "Single Sign-On is not yet provisioned for this organization."
    case "organization/licence_expired":
      return "The organization subscription has expired. Please contact your administrator."
    case "organization/organization_does_not_exist":
      return "Organization not found."
    case "organization/organization_member_does_not_exist":
      return "You are not a member of this organization."
    case "organization/base_info_not_available":
      return "Organization information is currently unavailable. Please try again."
    case "auth/popup-closed-by-user":
      return "Sign-in popup was closed before completing authentication."
    case "auth/cancelled-popup-request":
      return "Sign-in popup was cancelled."
    case "auth/popup-blocked":
      return "Sign-in popup was blocked by your browser. Please allow popups for this site."
    default:
      if (typeof code === "string" && code.includes("sso/required")) {
        return "Single Sign-On is required to access this organization."
      }
      return typeof code === "string" &&
        (code.startsWith("auth/") || code.startsWith("organization/"))
        ? `Authentication failed (${code})`
        : code
  }
}
